import { calcMax, calcMin } from './statistics';

type ValueType = 'time' | 'duration' | 'distance' | 'count';

type HistogramItem = {
  range: [number, number];
  value: number;
};

const HOUR = 3600;
const MINUTE = 60;
const KILO_METER = 1000;

const getNiceTickStep = (type: ValueType, range: number): number => {
  if (type === 'time' || type === 'duration') {
    if (range >= 18 * HOUR) {
      // 18時間以上の場合、6時間刻み
      return 6 * HOUR;
    }
    if (range >= 3 * HOUR) {
      // 3時間以上18時間未満の場合、3時間刻み
      return 3 * HOUR;
    }
    if (range >= 30 * MINUTE) {
      // 30分以上3時間未満の場合、30分刻み
      return 30 * MINUTE;
    }
    if (range >= 5 * MINUTE) {
      // 5分以上30分未満の場合、5分刻み
      return 5 * MINUTE;
    }
    // 5分未満の場合、1分刻み
    return 60;
  }
  if (type === 'distance' || type === 'count') {
    const digits = Math.floor(Math.log10(Math.abs(range)));
    const firstDigit = Math.floor(range / Math.pow(10, digits));
    const secondDigit = Math.floor(
      (range % Math.pow(10, digits)) / Math.pow(10, digits - 1)
    );

    if (firstDigit >= 8) {
      // 800...以上の場合、400...刻み
      return 4 * Math.pow(10, digits);
    }
    if (firstDigit >= 6) {
      // 600...以上800...未満の場合、200...刻み
      return 2 * Math.pow(10, digits);
    }
    if (firstDigit >= 5) {
      // 500...以上600...未満の場合、250...刻み
      return 25 * Math.pow(10, digits - 1);
    }
    if (firstDigit >= 3) {
      // 300...以上500...未満の場合、200...刻み
      return 2 * Math.pow(10, digits);
    }
    if (firstDigit >= 2) {
      // 200...以上300...未満の場合、100...刻み
      return Math.pow(10, digits);
    }
    if (secondDigit >= 5) {
      // 150...以上200...未満の場合、50...刻み
      return 5 * Math.pow(10, digits - 1);
    }
    // 100...以上150...未満の場合、50...刻み
    return 5 * Math.pow(10, digits - 1);
  }
  return 1;
};

const calculateNiceTicks = (
  type: ValueType,
  min: number,
  max: number
): number[] => {
  if (min >= max) {
    return [min];
  }
  const step = getNiceTickStep(type, max - min);

  // 最小値と最大値を刻み幅に合わせて丸める(メモリがグラフ内に収まるよう、最小値は切り上げて最大値は切り下げる)
  // ただし例外的に、時刻の場合は最大値が24時間を超えないようにする
  const roundedMin = Math.ceil(min / step) * step;
  const roundedMax =
    type === 'time' && max > 24 * HOUR
      ? 24 * HOUR
      : Math.floor(max / step) * step;

  // 丸めた最小値から最大値まで、刻み幅ごとにメモリを生成する
  const tickCount = Math.round((roundedMax - roundedMin) / step);
  return Array.from({ length: tickCount + 1 }, (_, index: number): number => {
    return roundedMin + step * index;
  });
};

const calculateHistogramMaxValue = (
  ...histogramDatas: HistogramItem[][]
): number => {
  return Math.max(
    ...histogramDatas.map((histogramData: HistogramItem[]): number => {
      return Math.max(
        ...histogramData.map((item: HistogramItem): number => {
          return item.value;
        }),
        0
      );
    }),
    0
  );
};

const getNiceHistogramBucket = (
  type: ValueType,
  range: [number, number] | null
): { count: number } | { step: number } => {
  const rangeDiff = range ? range[1] - range[0] : null;

  if (type === 'time') {
    return { step: HOUR };
  }
  if (type === 'duration') {
    if (rangeDiff === null || rangeDiff > 30 * HOUR) {
      // 範囲が不明または30時間を超える場合、25個分割のヒストグラムにする
      return { count: 25 };
    }
    if (rangeDiff < 40 * MINUTE) {
      // 範囲が40分未満の場合、1分刻みのヒストグラムにする
      return { step: MINUTE };
    }
    // キリのよい幅の候補を用意し、それぞれで分割した時の個数が最も25個に近い刻み幅を見つける
    // 計算上、40分以上30時間以下のどのような範囲であっても刻み幅の数は20〜30個に収まる
    const candidates = [2, 3, 4, 5, 6, 8, 10, 15, 20, 30, 40, 50, 60];
    const best = candidates
      .map((value: number): { step: number; count: number } => {
        return {
          step: value * MINUTE,
          count: Math.ceil(rangeDiff / (value * MINUTE)),
        };
      })
      .reduce(
        (currentBest, next): typeof currentBest => {
          if (Math.abs(next.count - 25) < Math.abs(currentBest.count - 25)) {
            return next;
          }
          return currentBest;
        },
        { step: 0, count: Infinity }
      );
    return { step: best.step };
  }
  if (type === 'distance') {
    if (rangeDiff === null || rangeDiff > 300 * KILO_METER) {
      // 範囲が不明または300kmを超える場合、25個分割のヒストグラムにする
      return { count: 25 };
    }
    if (rangeDiff < 4 * KILO_METER) {
      // 範囲が4km未満の場合、100m刻みのヒストグラムにする
      return { step: 0.1 * KILO_METER };
    }
    // キリのよい幅の候補を用意し、それぞれで分割した時の個数が最も25個に近い刻み幅を見つける
    // 計算上、4km以上300km未満のどのような範囲であっても刻み幅の数は20〜30個に収まる
    const candidates = [
      0.2, 0.3, 0.4, 0.5, 0.6, 0.8, 1, 1.5, 2, 3, 4, 5, 6, 8, 10,
    ];
    const best = candidates
      .map((value: number): { step: number; count: number } => {
        return {
          step: value * KILO_METER,
          count: Math.ceil(rangeDiff / (value * KILO_METER)),
        };
      })
      .reduce(
        (currentBest, next): typeof currentBest => {
          if (Math.abs(next.count - 25) < Math.abs(currentBest.count - 25)) {
            return next;
          }
          return currentBest;
        },
        { step: 0, count: Infinity }
      );
    return { step: best.step };
  }
  // それ以外の場合、25個分割のヒストグラムにする
  return { count: 25 };
};

const createHistogram = (data: number[], bins: number): HistogramItem[] => {
  const max = calcMax(data);
  const min = calcMin(data);
  const step = (max - min) / bins;

  const histogramData = Array.from(
    { length: bins },
    (_, index: number): HistogramItem => ({
      range: [min + step * index, min + step * (index + 1)],
      value: 0,
    })
  );

  data.forEach((value: number): void => {
    const index = Math.floor((value - min) / step);
    if (0 <= index && index < histogramData.length) {
      histogramData[index].value += 1;
    }
  });

  return histogramData;
};

export type { ValueType };
export {
  calculateNiceTicks,
  calculateHistogramMaxValue,
  getNiceHistogramBucket,
  createHistogram,
};
